/**
 * In-session Assembly View Position store (F-024, UAT-09).
 *
 * A reviewer's *place* in a Trial's Assembly review list — which Sections and
 * Subsections are expanded, the active Test-ID filter, and the scroll offset —
 * captured as it changes (write-through) and re-seeded on the next in-session
 * arrival at that Trial's assembly. The nested Test Case route unmounts the
 * assembly subtree (`uat.tsx`), destroying its `useState`; this module survives
 * that unmount because it lives outside the route tree (research R1).
 *
 * Browser-memory only (a module-level `Map`): NOT persisted to
 * localStorage/sessionStorage, NOT encoded in the URL, gone on a full reload —
 * the module re-evaluates to an empty Map (FR-009 / I-NS-06). Keyed by
 * `trialId`, so each Trial restores only its own place (FR-007 / I-NS-01).
 *
 * View-only (FR-010): capturing/restoring a place issues no request, mutates no
 * Test Script data, and emits no audit record. This is web presentation-tier
 * view state, not a generator domain concept — per GLOSSARY.md's scope note it
 * is intentionally NOT added to the glossary (data-model.md ruling; §XI-exempt
 * component-local UI state, research R7). Mirrors the module-level `lib/` state
 * convention of `auth.ts` (research R1).
 */

/**
 * A snapshot of where the reviewer was in the Assembly review list. Transient;
 * discarded on a full page reload.
 */
export type AssemblyViewPosition = {
  /** Section ids expanded at capture (the user's real set, not the filter-forced view). FR-001 */
  readonly openSectionIds: readonly string[];
  /** Subsection ids expanded at capture. FR-002 */
  readonly openSubsectionIds: readonly string[];
  /** Active Test-ID search filter text ('' = no filter). FR-012 */
  readonly query: string;
  /** Scroll offset of the AppShell content container, in px. FR-003 */
  readonly scrollTop: number;
};

/**
 * Read/capture behaviour contract for the per-Trial position store. (A `type`,
 * not an `interface` — the repo's `consistent-type-definitions` lint rule
 * forbids `interface`; the data-model's illustrative `interface` is realised as
 * this equivalent object type.)
 */
export type AssemblyPositionStore = {
  /** The captured place for a Trial, or `undefined` if none was captured this session. */
  readPosition(trialId: string): AssemblyViewPosition | undefined;
  captureOpenSections(trialId: string, ids: readonly string[]): void;
  captureOpenSubsections(trialId: string, ids: readonly string[]): void;
  captureQuery(trialId: string, query: string): void;
  captureScrollTop(trialId: string, scrollTop: number): void;
};

const DEFAULT_POSITION: AssemblyViewPosition = {
  openSectionIds: [],
  openSubsectionIds: [],
  query: '',
  scrollTop: 0,
};

// Module-level, in-session, per-Trial. Dies when the tab reloads.
const positions = new Map<string, AssemblyViewPosition>();

// The merge base for a partial capture: the Trial's current entry, or the
// all-default position so each write-through handler stays independent (it
// never has to know the other fields' current values — data-model.md).
const base = (trialId: string): AssemblyViewPosition => positions.get(trialId) ?? DEFAULT_POSITION;

export const assemblyPositionStore: AssemblyPositionStore = {
  readPosition: (trialId) => positions.get(trialId),
  captureOpenSections: (trialId, ids) => {
    positions.set(trialId, { ...base(trialId), openSectionIds: ids });
  },
  captureOpenSubsections: (trialId, ids) => {
    positions.set(trialId, { ...base(trialId), openSubsectionIds: ids });
  },
  captureQuery: (trialId, query) => {
    positions.set(trialId, { ...base(trialId), query });
  },
  captureScrollTop: (trialId, scrollTop) => {
    positions.set(trialId, { ...base(trialId), scrollTop });
  },
};

/**
 * Clamp a restored scroll offset to the current content's scrollable range
 * `[0, maxScrollTop]`, where `maxScrollTop = scrollHeight − clientHeight`. A
 * shorter list (or one not tall enough to scroll, `maxScrollTop ≤ 0`) lands at
 * the nearest valid position rather than blank space past the end (FR-003 #2 /
 * I-NS-04).
 */
export const clampScrollTop = (value: number, maxScrollTop: number): number =>
  Math.max(0, Math.min(value, maxScrollTop));

/** Test-only: clear every captured place (the store is a module singleton). */
export const __resetAssemblyPositions = (): void => {
  positions.clear();
};
